import { jwtDecode } from 'jwt-decode';
import { authApi } from '@/common/api/authApi';

export class SessionEndedError extends Error {
  constructor() {
    super('no_session');
    this.name = 'SessionEndedError';
  }
}

// Refetch a minute early so a token handed out here doesn't expire mid-request.
const EXPIRY_LEEWAY_SECONDS = 60;

let cached: { token: string; expiresAt: number } | undefined;
let inFlight: Promise<string> | undefined;

const expiresAtOf = (token: string): number | undefined => {
  try {
    return jwtDecode<{ exp?: number }>(token).exp;
  } catch {
    return undefined;
  }
};

const requestAccessToken = async (): Promise<string> => {
  const response = await authApi.token.$get();
  if (response.status === 401) {
    cached = undefined;
    throw new SessionEndedError();
  }
  if (response.status !== 200) {
    throw new Error(`token_unavailable: ${response.status}`);
  }
  const { accessToken } = await response.json();
  const expiresAt = expiresAtOf(accessToken);
  cached = expiresAt === undefined ? undefined : { token: accessToken, expiresAt };
  return accessToken;
};

export const fetchAccessToken = async ({
  cacheMode = 'on',
}: {
  cacheMode?: 'on' | 'off';
} = {}): Promise<string> => {
  if (
    cacheMode === 'on' &&
    cached &&
    cached.expiresAt - EXPIRY_LEEWAY_SECONDS > Date.now() / 1000
  ) {
    return cached.token;
  }
  inFlight ??= requestAccessToken().finally(() => {
    inFlight = undefined;
  });
  return inFlight;
};
